import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindMid.css';
import '../../styles/hot-table-status-pills.css';
import { apiErrToMessage } from '../../utils/apiError';
import { hotStatusPillRenderer, plainStatusForExport } from '../../utils/hotTableStatusRenderer';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import * as XLSX from 'xlsx';

import {
    Card,
    Row,
    Col,
    Button,
    Modal,
    List,
    Space,
    Typography,
    Divider,
    Empty,
    Spin,
    message,
} from 'antd';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import {
    HistoryOutlined,
    DownloadOutlined,
    SearchOutlined,
    ReloadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const COL_WIDTHS_STORAGE_KEY = 'findMid_colWidths';
const DEFAULT_COL_WIDTHS = [250, 130, 130, 300, 120, 200, 100, 250, 100];

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const arr = JSON.parse(saved);
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

registerAllModules();

function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

const FindMid = ({ title: _title = 'MID 조회' }) => {
    const hotRef = useRef(null);
    const [loading, setLoading] = useState(false); // 🔹 로딩 상태 추가
    const [showGuide, _setShowGuide] = useState(() => {
        try {
            const saved = localStorage.getItem('findmid_showGuide');
            return saved !== null ? JSON.parse(saved) : true; // 기본 true
        } catch {
            return true;
        }
    });

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [maxRows, setMaxRows] = useState(20);
    const [tableData, setTableData] = useState([]); // ← 초기엔 빈 배열
    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        if (saved && saved.length === DEFAULT_COL_WIDTHS.length) return [...saved];
        return [...DEFAULT_COL_WIDTHS];
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'findMid' },
                });

                const rows = data?.success ? data.max_rows : 20;
                setMaxRows(rows);
                setTableData(Array.from({ length: rows }, () => Array(9).fill(''))); // ✅ 행 수 반영
                console.log(`✅ MAX_ROWS = ${rows} (${data.source || 'default'})`);
            } catch (err) {
                console.error('⚠️ 행 제한 조회 실패:', err);
                setMaxRows(20);
                setTableData(Array.from({ length: 20 }, () => Array(9).fill(''))); // fallback
            }
        })();
    }, []);

    const handleMidSearch = async () => {
        if (loading) return;
        setLoading(true);

        try {
            const currentData = [...tableData];
            const hot = hotRef.current.hotInstance;

            // ✅ 유효한 행만 추출 (index 포함)
            const validProducts = currentData
                .map((row, idx) => {
                    const url = row[0]?.trim();
                    if (url && isValidUrl(url)) {
                        currentData[idx][8] = '<div class="loading-spinner"></div>';
                        hot.setCellMeta(idx, 8, 'className', 'processing-row');
                        return { index: idx, url };
                    }
                    return null;
                })
                .filter(Boolean);

            if (validProducts.length === 0) {
                message.warning('상품 URL을 입력한 행이 1개 이상 있어야 합니다.');
                setLoading(false);
                return;
            }

            // ✅ 스트리밍 요청을 위해 fetch 사용
            const token = AuthService.getToken();
            const sessionId = AuthService.getSessionId();
            const baseURL = AuthService.authAxios.defaults.baseURL || '';

            const response = await fetch(`${baseURL}/product/findmid2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'X-Session-ID': sessionId || '',
                },
                body: JSON.stringify({
                    products: validProducts,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw { response: { status: response.status, data: errorData } };
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop(); // 마지막 미완성 줄은 버퍼에 남김

                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const data = JSON.parse(line);

                        if (data.results && Array.isArray(data.results)) {
                            const results = data.results;

                            // ✅ 결과 반영 (index 기준으로 정확히 매칭)
                            results.forEach(item => {
                                const i = item.index;
                                currentData[i][1] = item.productMid || '';
                                currentData[i][2] = item.parentMid || '';
                                currentData[i][3] = item.title || '';
                                currentData[i][4] = item.mallName || '';
                                currentData[i][5] = item.image || '';
                                currentData[i][6] = item.price ? item.price.toLocaleString() : '';
                                currentData[i][7] = Array.isArray(item.detailInfo)
                                    ? item.detailInfo.join(', ')
                                    : item.detailInfo || '';

                                if (item.productMid || item.title) {
                                    currentData[i][8] = '성공';
                                    hot.setCellMeta(i, 8, 'className', 'completed-row');
                                } else if (item.error) {
                                    currentData[i][8] = '실패';
                                    hot.setCellMeta(i, 8, 'className', 'error-row');
                                } else {
                                    currentData[i][8] = '값 없음';
                                    hot.setCellMeta(i, 8, 'className', 'completed-row');
                                }
                            });

                            // ✅ 실시간 테이블 갱신
                            setTableData([...currentData]);
                            hot.render();
                        }

                        if (data.done) {
                            applyQuotaIfAny(data.quota);
                        }

                        if (data.success === false && data.message) {
                            message.error(data.message);
                        }
                    } catch (e) {
                        console.error('NDJSON line parse error:', e, line);
                    }
                }
            }
        } catch (err) {
            // ✅ 진짜 네트워크/서버 오류만 처리
            const status = err?.response?.status;
            const data = err?.response?.data;
            message.error(apiErrToMessage(data, status));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setTableData([...Array.from({ length: maxRows }, () => Array(9).fill(''))]);
    };

    const _handleSubmit = e => {
        e.preventDefault();
        handleMidSearch();
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '정말 삭제하시겠습니까?',
            content: '삭제된 기록은 되돌릴 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('성공적으로 삭제되었습니다.');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제에 실패했습니다.');
                }
            },
        });
    };

    const fetchHistory = async (nextOffset = 0, append = false) => {
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'findmid', offset: nextOffset },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) setHasMore(true);
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const handleExportExcel = () => {
        try {
            const headers = [
                '키워드',
                '상품 URL',
                '단일 MID',
                '원부 MID',
                '상품명',
                '스토어명',
                '썸네일',
                '가격',
                '태그',
                '상태',
            ];

            // ✅ 유효한 데이터만 필터링 (한 셀이라도 값이 있는 행만)
            const filteredData = tableData.filter(row => row.some(cell => cell && cell !== ''));

            // ✅ 데이터 없을 시 경고 후 중단
            if (filteredData.length === 0) {
                message.warning('저장할 결과가 없습니다.');
                return;
            }

            // JSON 변환용: 헤더 키에 맞게 매핑
            const exportData = filteredData.map(row => ({
                '상품 URL': row[0] || '',
                '단일 MID': row[1] || '',
                '원부 MID': row[2] || '',
                상품명: row[3] || '',
                스토어명: row[4] || '',
                썸네일: row[5] || '',
                가격: row[6] || '',
                태그: row[7] || '',
                상태: plainStatusForExport(row[8]),
            }));

            // ✅ 워크북 생성
            const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'MID조회결과');

            // ✅ 파일명 생성
            const now = new Date();
            const timestamp = now.toISOString().slice(0, 19).replace(/[-T:]/g, '');
            const filename = `MID조회결과_${timestamp}.xlsx`;

            // ✅ 파일 다운로드
            XLSX.writeFile(workbook, filename);

            console.log('✅ 엑셀 다운로드 완료:', filename);
        } catch (error) {
            console.error('❌ 엑셀 다운로드 오류:', error);
            message.error('엑셀 다운로드 중 오류가 발생했습니다.');
        }
    };

    useEffect(() => {
        localStorage.setItem('findmid_showGuide', showGuide);
    }, [showGuide]);

    const applyQuotaIfAny = obj => {
        if (!obj || typeof obj !== 'object') return;
        const has = k => Object.prototype.hasOwnProperty.call(obj, k);
        if (has('used') || has('limit') || has('bonus') || has('remaining') || has('day')) {
            dispatchQuotaUpdated('findMid');
        }
    };

    return (
        <ProtectedMenuPage menuKey="findMid">
            <div className="find-mid-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '8px 16px' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Text type="secondary">사용 가능 행 수: </Text>
                                <Text strong>{maxRows}</Text>
                            </Col>
                            <Col>
                                <Space size="small">
                                    <Button
                                        type="primary"
                                        className="search-btn-premium"
                                        onClick={handleMidSearch}
                                        loading={loading}
                                        icon={<SearchOutlined />}
                                    >
                                        조회하기
                                    </Button>
                                    <Button
                                        className="page-action-btn"
                                        icon={<ReloadOutlined />}
                                        onClick={handleReset}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        className="history-btn"
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            setShowHistoryModal(true);
                                            fetchHistory();
                                        }}
                                    >
                                        기록
                                    </Button>
                                    <Button
                                        className="excel-button"
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportExcel}
                                        title="현재 조인된 데이터를 엑셀로 저장"
                                    />
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </div>

                <div className="page-content-inner">
                    <div className="find-mid-table">
                        <HotTable
                            ref={hotRef}
                            data={tableData}
                            colHeaders={[
                                '상품 URL <span class="text-danger">*</span>',
                                '단일 MID',
                                '원부 MID',
                                '상품명',
                                '스토어명',
                                '썸네일',
                                '가격',
                                '태그',
                                '상태',
                            ]}
                            contextMenu={{
                                items: {
                                    undo: {
                                        name: '되돌리기',
                                        hidden: function () {
                                            return !this.getPlugin('undoRedo')?.isUndoAvailable();
                                        },
                                    },
                                    redo: {
                                        name: '다시 실행하기',
                                        hidden: function () {
                                            return !this.getPlugin('undoRedo')?.isRedoAvailable();
                                        },
                                    },
                                    copy: { name: '복사' },
                                    cut: { name: '잘라내기' },
                                },
                            }}
                            rowHeaders={true}
                            manualColumnResize={true}
                            stretchH="all"
                            height="100%"
                            width="100%"
                            colWidths={colWidths}
                            afterColumnResize={(newSize, column) => {
                                setColWidths(prev => {
                                    const next = [...prev];
                                    next[column] = newSize;
                                    saveColWidths(next);
                                    return next;
                                });
                            }}
                            cells={(row, col) => {
                                const align =
                                    col === 1 || col === 2 || col === 4 || col === 8
                                        ? 'htCenter'
                                        : col === 6
                                          ? 'htRight'
                                          : 'htLeft';
                                const base =
                                    col === 0
                                        ? { readOnly: false, className: 'required-column' }
                                        : {
                                              readOnly: true,
                                              renderer:
                                                  col === 8 ? hotStatusPillRenderer : undefined,
                                          };
                                return {
                                    ...base,
                                    className: [base.className, align].filter(Boolean).join(' '),
                                };
                            }}
                            beforeChange={(changes, source) => {
                                if (!changes) return;
                                const isPaste = source === 'CopyPaste.paste';
                                if (isPaste) {
                                    const maxRowTouched = Math.max(...changes.map(([row]) => row));
                                    if (maxRowTouched + 1 > maxRows) {
                                        message.error(
                                            `최대 ${maxRows}행까지만 입력할 수 있습니다.`
                                        );
                                        return false;
                                    }
                                }
                            }}
                            licenseKey="non-commercial-and-evaluation"
                        />
                    </div>
                </div>

                <Modal
                    title="MID 조회 기록"
                    open={showHistoryModal}
                    onCancel={() => setShowHistoryModal(false)}
                    centered
                    mask={{ blur: true }}
                    footer={[
                        <Button key="close" onClick={() => setShowHistoryModal(false)}>
                            닫기
                        </Button>,
                    ]}
                    width={800}
                    className="history-modal"
                    bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    {!historyLoaded ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        </div>
                    ) : historyData.length === 0 ? (
                        <Empty description="조회 기록이 없습니다." />
                    ) : (
                        <List
                            dataSource={historyData}
                            renderItem={item => {
                                const parts = {};
                                item.input.split('|').forEach(pair => {
                                    const [key, value] = pair.split('=');
                                    parts[key] = value;
                                });

                                return (
                                    <List.Item
                                        actions={[
                                            <Button
                                                key="delete"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                삭제
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <Space
                                                    direction="vertical"
                                                    style={{ width: '100%' }}
                                                >
                                                    <Space split={<Divider type="vertical" />}>
                                                        <Text strong>
                                                            스토어: {parts.channel_name || '-'}
                                                        </Text>
                                                        <Text>키워드: {parts.keyword || '-'}</Text>
                                                    </Space>
                                                    <Text
                                                        type="secondary"
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        상품명: {parts.product_name || '-'}
                                                    </Text>
                                                </Space>
                                            }
                                            description={
                                                <div style={{ marginTop: '8px' }}>
                                                    <div
                                                        style={{
                                                            wordBreak: 'break-all',
                                                            marginBottom: '4px',
                                                        }}
                                                    >
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            URL: {parts.url || '-'}
                                                        </Text>
                                                    </div>
                                                    <Space split={<Divider type="vertical" />}>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            MID: {parts.syncNvMid || '-'}
                                                        </Text>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            원부: {parts.parentId || '-'}
                                                        </Text>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            {item.updated_at || item.searched_at}
                                                        </Text>
                                                    </Space>
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                        >
                            {hasMore && historyData.length > 0 && (
                                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                    <Button onClick={() => fetchHistory(offset, true)}>
                                        더보기
                                    </Button>
                                </div>
                            )}
                        </List>
                    )}
                </Modal>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { FindMid };

FindMid.propTypes = {
    title: PropTypes.string,
};
